import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cognitoForgotPassword, useEffectOnError, useEffectOnSuccess } from '@guryou/redux';
import logo from 'assets/img/logo.png';
import { authInputFocusProps } from './authInputFocusProps';

const ForgotPassword = ({ setView, branding }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accent = branding?.accentColor ?? 'var(--feel-primary)';
  const logoSrc = branding?.logoUrl ? branding.logoUrl : logo;
  const [isSuccess, setIsSuccess] = useState(false);

  // Create Zod schema with translations
  const forgotPasswordSchema = z.object({
    email: z
      .string()
      .min(1, t('FORGOT_PASSWORD_MODAL.validation.emailRequired'))
      .email(t('FORGOT_PASSWORD_MODAL.validation.emailInvalid')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onBlur',
  });

  const onSubmit = data => {
    dispatch(cognitoForgotPassword(data.email));
  };

  useEffectOnSuccess(
    state => state.forgotPassword,
    () => setIsSuccess(true)
  );

  useEffectOnError(
    state => state.forgotPassword,
    () => setIsSuccess(false)
  );

  if (isSuccess) {
    return (
      <div className="w-full max-w-md mx-auto">
        {/* Success State */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('FORGOT_PASSWORD_MODAL.resetLinkSent')}</h2>
          <p className="text-sm text-gray-600 mb-8">{t('FORGOT_PASSWORD_MODAL.checkYourEmail')}</p>
          <button
            type="button"
            onClick={() => setView('login')}
            className="inline-flex items-center text-sm font-semibold transition-colors hover:underline"
            style={{ color: accent }}
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
              />
            </svg>
            {t('FORGOT_PASSWORD_MODAL.backToLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header with Logo */}
      <div className="text-center border-b border-gray-100 pb-6">
        <div className="flex justify-center mb-4">
          <img src={logoSrc} alt={branding?.brandName || 'Logo'} className="h-12 w-auto max-w-[200px] object-contain" />
        </div>
        {branding?.brandName ? <p className="text-sm font-medium text-gray-800 mb-1">{branding.brandName}</p> : null}
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('FORGOT_PASSWORD_MODAL.forgotPassword')}</h2>
        <p className="text-sm text-gray-600">{t('FORGOT_PASSWORD_MODAL.resetInstructions')}</p>
      </div>

      {/* Form Content */}
      <div className="space-y-6 p-2 mt-2">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              {t('GENERAL.email')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                  />
                </svg>
              </div>
              <input
                type="email"
                id="email"
                className={`block w-full pl-10 pr-3 py-3 border rounded-lg text-sm placeholder-gray-500 focus:outline-none transition-colors ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                }`}
                {...authInputFocusProps(accent, !!errors.email, register('email'))}
                placeholder={t('FORGOT_PASSWORD_MODAL.enterYourEmail')}
              />
            </div>
            {errors.email && (
              <p className="mt-2 text-sm text-red-600 flex items-center" role="alert">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
            style={{ backgroundColor: accent }}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {t('FORGOT_PASSWORD_MODAL.sendingResetLink')}
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                {t('GENERAL.send')}
              </>
            )}
          </button>
        </form>
      </div>

      {/* Footer with Back to Login Link */}
      <div className="py-6 bg-gray-50 border-t border-gray-100">
        <div className="text-center">
          <button
            type="button"
            onClick={() => setView('login')}
            className="inline-flex items-center text-sm font-semibold transition-colors hover:underline"
            style={{ color: accent }}
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
              />
            </svg>
            {t('FORGOT_PASSWORD_MODAL.backToLogin')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
